import { useBeforeLeave, type RouteSectionProps } from '@solidjs/router';
import { onCleanup, onMount } from 'solid-js';

/** Covers the longest leave animation in styles.css (`page-leave` 300 ms, `page-leave-fade` 350 ms). */
export const PAGE_LEAVE_MS = 350;

/**
 * Router root: keeps a snapshot of the outgoing screen under the incoming one for `PAGE_LEAVE_MS`.
 *
 * The router unmounts the old route the moment the new one renders, and DivKit tears its DOM down in
 * `$destroy` — so the copy is taken in `useBeforeLeave`, while the screen is still intact, and shown once
 * the live node is gone. A static clone, not a second live instance: no card-variable capture, no
 * duplicate analytics, nothing to dispose but a timer.
 */
export const RouteCrossfade = (props: RouteSectionProps) => {
  let live!: HTMLDivElement;
  let ghosts!: HTMLDivElement;
  let pending: HTMLElement | undefined;

  useBeforeLeave(() => {
    const screen = live.querySelector<HTMLElement>(':scope > .app-shell');
    if (!screen) return;
    const ghost = screen.cloneNode(true) as HTMLElement;
    // A screen that was still sliding in must not slide in again as a ghost.
    ghost.classList.remove('page-enter');
    ghost.classList.add('route-ghost');
    // The router scrolls to the top for the new screen; the ghost stays where the user was looking.
    ghost.style.top = `${-window.scrollY}px`;
    pending = ghost;
  });

  const show = (ghost: HTMLElement) => {
    ghosts.append(ghost);
    const timer = window.setTimeout(() => ghost.remove(), PAGE_LEAVE_MS);
    onCleanup(() => window.clearTimeout(timer));
  };

  onMount(() => {
    const observer = new MutationObserver((records) => {
      const left = records.some((record) =>
        [...record.removedNodes].some((node) => node instanceof HTMLElement && node.classList.contains('app-shell')),
      );
      if (!left || !pending) return;
      show(pending);
      pending = undefined;
    });
    observer.observe(live, { childList: true });
    onCleanup(() => observer.disconnect());
  });

  return (
    <div class="route-stack">
      <div class="route-ghosts" ref={ghosts} aria-hidden="true" />
      <div class="route-live" ref={live}>
        {props.children}
      </div>
    </div>
  );
};
